import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Lock, Crown, Gem, Flame, Package, Settings, Clock, Eye, MapPin,
  ArrowRight, Tag, User, ChevronLeft, ChevronRight, FileText, Shield
} from 'lucide-react';
import { channelApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const CATEGORY_META = {
  gold_trade: { name: 'Gold Trade', icon: Gem, color: '#D4AF37', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  oil_energy: { name: 'Oil & Energy', icon: Flame, color: '#1E3A5F', bg: 'bg-blue-50', text: 'text-blue-800', border: 'border-blue-200' },
  commodities: { name: 'Commodities', icon: Package, color: '#16A34A', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  machinery: { name: 'Machinery', icon: Settings, color: '#7C3AED', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
};

const TYPE_LABELS = {
  trade_deal: 'Trade Deal',
  mandate: 'Mandate',
  commodity_offer: 'Commodity Offer',
  document: 'Intelligence',
  image: 'Gallery',
};

const PremiumChannelPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const activeCategory = searchParams.get('category') || '';
  const activeType = searchParams.get('type') || '';

  useEffect(() => {
    fetchPosts();
  }, [page, activeCategory, activeType]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (activeCategory) params.category = activeCategory;
      if (activeType) params.post_type = activeType;

      const { data } = await channelApi.getPosts(params);
      setPosts(data.posts || []);
      setTotalPages(data.pages || 1);
      setHasAccess(data.has_premium_access || false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const setFilter = (key, val) => {
    const params = new URLSearchParams(searchParams);
    if (val) params.set(key, val);
    else params.delete(key);
    params.delete('page');
    setSearchParams(params);
    setPage(1);
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      <Header />

      {/* Hero */}
      <section className="bg-[#0A192F] py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#D4AF37] rounded-lg flex items-center justify-center">
              <Crown className="w-5 h-5 text-[#0A192F]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">Premium Channel</h1>
              <p className="text-slate-400 text-sm">Exclusive B2B deals, mandates & market intelligence</p>
            </div>
          </div>
          {!hasAccess && (
            <div className="mt-4 flex items-center gap-3">
              <Button
                onClick={() => navigate('/membership')}
                className="bg-[#D4AF37] hover:bg-[#C49F2D] text-[#0A192F] font-bold rounded-md h-10 px-5 text-sm"
                data-testid="channel-upgrade-btn"
              >
                <Crown className="w-4 h-4 mr-1.5" /> Upgrade for Full Access
              </Button>
              <span className="text-slate-500 text-xs">Premium content is locked for free users</span>
            </div>
          )}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-col lg:flex-row gap-6">

          {/* Sidebar - Categories & Filters */}
          <aside className="lg:w-56 shrink-0 space-y-4">
            <div className="bg-white border border-slate-200 p-4">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Categories</h3>
              <button
                onClick={() => setFilter('category', '')}
                className={`w-full text-left px-3 py-2 text-sm rounded-md mb-1 transition-colors ${!activeCategory ? 'bg-[#0A192F] text-white font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}
                data-testid="filter-all"
              >
                All Posts
              </button>
              {Object.entries(CATEGORY_META).map(([id, cat]) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={id}
                    onClick={() => setFilter('category', id)}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm rounded-md mb-1 transition-colors ${activeCategory === id ? `${cat.bg} ${cat.text} font-semibold` : 'text-slate-600 hover:bg-slate-50'}`}
                    data-testid={`filter-${id}`}
                  >
                    <Icon className="w-4 h-4" /> {cat.name}
                  </button>
                );
              })}
            </div>

            <div className="bg-white border border-slate-200 p-4">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Post Type</h3>
              {Object.entries(TYPE_LABELS).map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setFilter('type', activeType === id ? '' : id)}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md mb-1 transition-colors ${activeType === id ? 'bg-slate-100 font-semibold text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
                  data-testid={`filter-type-${id}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </aside>

          {/* Feed */}
          <main className="flex-1 space-y-4" data-testid="channel-feed">
            {loading ? (
              [...Array(4)].map((_, i) => (
                <div key={i} className="bg-white border border-slate-200 p-6 animate-pulse">
                  <div className="h-4 bg-slate-200 rounded w-1/3 mb-3" />
                  <div className="h-3 bg-slate-200 rounded w-full mb-2" />
                  <div className="h-3 bg-slate-200 rounded w-2/3" />
                </div>
              ))
            ) : posts.length === 0 ? (
              <div className="bg-white border border-slate-200 p-12 text-center">
                <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">No posts found</p>
              </div>
            ) : (
              posts.map((post) => {
                const cat = CATEGORY_META[post.category] || {};
                const CatIcon = cat.icon || Package;
                return (
                  <article
                    key={post.post_id}
                    className="bg-white border border-slate-200 hover:border-slate-300 transition-colors group"
                    data-testid={`post-${post.post_id}`}
                  >
                    {/* Post header */}
                    <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-[#0A192F] flex items-center justify-center text-white text-xs font-bold shrink-0">
                          {post.author_name?.charAt(0) || 'A'}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{post.author_name}</p>
                          <p className="text-xs text-slate-400">{formatDate(post.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className={`${cat.bg} ${cat.text} ${cat.border} border text-[10px] font-semibold`}>
                          <CatIcon className="w-3 h-3 mr-0.5" />
                          {cat.name}
                        </Badge>
                        <Badge variant="outline" className="text-[10px]">{TYPE_LABELS[post.post_type] || post.post_type}</Badge>
                        {post.is_premium && <Badge className="bg-[#D4AF37] text-[#0A192F] text-[10px] font-bold border-0"><Crown className="w-3 h-3 mr-0.5" />Premium</Badge>}
                      </div>
                    </div>

                    {/* Post content */}
                    <div className="px-5 pb-4">
                      <Link to={`/channel/${post.post_id}`} className="block">
                        <h2 className="text-base font-bold text-[#0A192F] group-hover:text-[#EA580C] transition-colors mb-2 leading-snug">
                          {post.title}
                        </h2>
                      </Link>

                      <div className="relative">
                        <p className={`text-sm text-slate-600 leading-relaxed ${post.is_locked ? 'line-clamp-3' : 'line-clamp-5'}`}>
                          {post.content}
                        </p>
                        {post.is_locked && (
                          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/70 to-white flex items-end justify-center pb-2" data-testid={`post-lock-${post.post_id}`}>
                            <Button
                              onClick={() => navigate('/membership')}
                              size="sm"
                              className="bg-[#0A192F] hover:bg-[#172A46] text-white rounded-md h-8 px-4 text-xs font-semibold"
                            >
                              <Lock className="w-3 h-3 mr-1.5" /> Unlock Full Post
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Meta tags */}
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        {post.deal_value && (
                          <span className="text-xs font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded">{post.deal_value}</span>
                        )}
                        {post.origin && (
                          <span className="text-xs text-slate-500 flex items-center gap-0.5"><MapPin className="w-3 h-3" />{post.origin}</span>
                        )}
                        {post.destination && (
                          <span className="text-xs text-slate-500">→ {post.destination}</span>
                        )}
                        {post.tags?.map((tag, i) => (
                          <span key={i} className="text-xs text-slate-400 flex items-center gap-0.5">
                            <Tag className="w-2.5 h-2.5" />{tag}
                          </span>
                        ))}
                        <span className="text-xs text-slate-400 ml-auto flex items-center gap-1"><Eye className="w-3 h-3" />{post.views}</span>
                      </div>
                    </div>

                    {/* Post image preview */}
                    {post.images?.length > 0 && (
                      <div className="px-5 pb-5">
                        <div className="h-48 bg-slate-100 rounded-lg overflow-hidden">
                          <img src={post.images[0]} alt="" className="w-full h-full object-cover" />
                        </div>
                      </div>
                    )}
                  </article>
                );
              })
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 py-4">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-600">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PremiumChannelPage;
